import React, { useEffect, useState, useContext } from "react";
import { formatPhone } from "../../utils/formatPhone";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const UserList = ({ onEdit }) => {
  const {
    tenant_id,
    user,
    loadingUser,
  } = useContext(AuthContext);
  
  // Get is_primary from user object
  const isPrimary = user?.is_primary === true;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);

  useEffect(() => {
    if (loadingUser || !user || !tenant_id) {
      console.log("â³ Waiting for:", {
        loadingUser,
        hasUser: !!user,
        hasTenantId: !!tenant_id,
      });
      return;
    }

    const fetchUsers = async () => {
      try {
        const config = {
          params: isPrimary ? { user: JSON.stringify(user) } : { tenant_id },
        };

        const res = await secureAxios.get("/api/users", config);
        setUsers(res.data);
        setLoading(false);
      } catch (err) {
        console.error("âŒ Error fetching users:", err);
        if (err.response?.status === 401) {
          console.error("ðŸ” Authentication failed");
        }
        setLoading(false);
      }
    };

    fetchUsers();
  }, [user, tenant_id, isPrimary, loadingUser]);

  const handleResetPassword = async (user) => {
    const confirmReset = window.confirm(
      `Are you sure you want to send a password reset email to ${user.name} (${user.email})?\n\n` +
        `This action will be logged in the audit trail.`
    );

    if (!confirmReset) return;

    try {
      await secureAxios.post(`/api/users/${user.user_id}/reset-password`, {
        email: user.email,
        name: user.name,
        tenant_id,
      });
      alert(
        "âœ… Password reset email sent!\n\n" +
          "The user will receive instructions to create a new password. " +
          "This action has been logged in the audit trail."
      );
    } catch (err) {
      console.error("âŒ Failed to send reset email:", err);
      alert("âŒ Error sending reset email.");
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!deleteAcknowledged) {
      alert("Please acknowledge the deletion warning before proceeding.");
      return;
    }

    try {
      await secureAxios.delete(`/api/users/${userToDelete.user_id}`, {
        data: { tenant_id },
      });
      setUsers(users.filter((u) => u.user_id !== userToDelete.user_id));
      setShowDeleteConfirm(null);
      setDeleteAcknowledged(false);
      alert("âœ… User deleted. This action has been logged in the audit trail.");
    } catch (err) {
      console.error("âŒ Failed to delete user:", err);
      if (err.response?.status === 409) {
        alert("âŒ Cannot delete user â€” they are assigned to other records.");
      } else {
        alert("âŒ Error deleting user.");
      }
    }
  };

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loadingUser || loading) return <p>Loading users...</p>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>User List</h2>
        <span className="badge bg-secondary">
          {filteredUsers.length} User{filteredUsers.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* HIPAA Notice */}
      <div className="alert alert-info mb-3">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Privacy Notice:</strong> This list contains Personally
        Identifiable Information (PII). Access and use this information only for
        authorized business purposes. All actions are logged and monitored.
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <div className="input-group">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search by name (minimum 3 characters)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {searchTerm && searchTerm.length < 3 && (
          <small className="text-muted">
            Enter at least 3 characters to search
          </small>
        )}
      </div>

      {/* Data Export Warning */}
      <div className="alert alert-warning mb-3">
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Data Protection:</strong> Do not export, screenshot, or share
        this information outside of authorized channels. Unauthorized disclosure
        of PII may result in disciplinary action and legal penalties.
      </div>

      {/* Desktop Table View */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.user_id}>
                <td>
                  <div>
                    <span>{user.name}</span>
                    {isPrimary && user.tenant_name && (
                      <div className="small text-muted">
                        <i className="bi bi-building me-1"></i>
                        {user.tenant_name}
                      </div>
                    )}
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{formatPhone(user.phone_number)}</td>
                <td>
                  <span
                    className={`badge ${
                      user.role?.toLowerCase() === "admin"
                        ? "bg-danger"
                        : "bg-primary"
                    }`}
                  >
                    {user.role}
                  </span>
                </td>
                <td>
                  <span
                    className={`badge ${
                      user.status?.toLowerCase() === "active"
                        ? "bg-success"
                        : "bg-secondary"
                    }`}
                  >
                    {user.status}
                  </span>
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onEdit(user)}
                      title="Edit user information"
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => handleResetPassword(user)}
                      title="Send password reset email"
                    >
                      <i className="bi bi-key"></i> Reset
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setShowDeleteConfirm(user)}
                      title="Delete user account"
                    >
                      <i className="bi bi-trash"></i> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* âœ… Mobile Card View */}
      <div className="d-md-none">
        {filteredUsers.map((user) => (
          <div key={user.user_id} className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h5 className="card-title mb-0">{user.name}</h5>
                <div>
                  <span
                    className={`badge ${
                      user.status?.toLowerCase() === "active"
                        ? "bg-success"
                        : "bg-secondary"
                    } me-1`}
                  >
                    {user.status}
                  </span>
                  <span
                    className={`badge ${
                      user.role?.toLowerCase() === "admin"
                        ? "bg-danger"
                        : "bg-primary"
                    }`}
                  >
                    {user.role}
                  </span>
                </div>
              </div>
              <p className="card-text mb-1">
                <i className="bi bi-envelope me-2"></i>
                <strong>Email:</strong> {user.email}
              </p>
              <p className="card-text mb-1">
                <i className="bi bi-phone me-2"></i>
                <strong>Phone:</strong> {formatPhone(user.phone_number)}
              </p>
              {isPrimary && (
                <p className="card-text mb-1">
                  <i className="bi bi-building me-2"></i>
                  <strong>Tenant:</strong> {user.tenant_name}
                </p>
              )}
              <div className="d-flex flex-column gap-2 mt-3">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => onEdit(user)}
                >
                  <i className="bi bi-pencil me-1"></i> Edit User
                </button>
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleResetPassword(user)}
                >
                  <i className="bi bi-key me-1"></i> Reset Password
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => setShowDeleteConfirm(user)}
                >
                  <i className="bi bi-trash me-1"></i> Delete User
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm User Deletion
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    setDeleteAcknowledged(false);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <h6>You are about to delete the following user:</h6>
                  <ul className="mb-0">
                    <li>
                      <strong>Name:</strong> {showDeleteConfirm.name}
                    </li>
                    <li>
                      <strong>Email:</strong> {showDeleteConfirm.email}
                    </li>
                    <li>
                      <strong>Role:</strong> {showDeleteConfirm.role}
                    </li>
                  </ul>
                </div>

                <div className="alert alert-warning">
                  <h6 className="alert-heading">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    Important Considerations:
                  </h6>
                  <ul className="mb-0">
                    <li>This action cannot be undone</li>
                    <li>The user will lose all system access immediately</li>
                    <li>Historical audit logs will be retained</li>
                    <li>Any active sessions will be terminated</li>
                    <li>This deletion will be logged with your user ID</li>
                  </ul>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="deleteAcknowledge"
                    checked={deleteAcknowledged}
                    onChange={(e) => setDeleteAcknowledged(e.target.checked)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="deleteAcknowledge"
                  >
                    <strong>
                      I understand this action is permanent and has been
                      properly authorized
                    </strong>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(null);
                    setDeleteAcknowledged(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={!deleteAcknowledged}
                  onClick={() => handleDeleteUser(showDeleteConfirm)}
                >
                  <i className="bi bi-trash me-1"></i>
                  Permanently Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-person-x display-1 text-muted"></i>
          <p className="mt-3 text-muted">
            {searchTerm
              ? "No users found matching your search."
              : "No users found."}
          </p>
        </div>
      )}

      {/* Footer Compliance Info */}
      <div className="mt-4 p-3 bg-light rounded">
        <small className="text-muted">
          <i className="bi bi-shield-check me-1"></i>
          <strong>Compliance Note:</strong> Regular user access reviews should
          be conducted quarterly. Inactive accounts should be disabled promptly.
          All user management activities are subject to audit under HIPAA
          Section 164.312(b).
        </small>
      </div>
    </div>
  );
};

export default UserList;